import { VerifiedOutlined } from '@mui/icons-material';
import { Alert, Paper, Stack } from '@mui/material';
import { ResourceState } from '../../../design-system';
import type { AsyncResource } from '../../../shared/useAsyncResource';
import type { CompanyLegalProfile } from '../model/types';
import { CompanyLegalDetailCard } from './CompanyLegalDetailCard';
import type { CompanyLegalEditableField } from './CompanyLegalFields';
import { SettingRow } from './SettingRow';

type AffiliateLegalDetailProps = {
  canWrite: boolean;
  canReadSeal: boolean;
  error?: string;
  resource: AsyncResource<CompanyLegalProfile[]>;
  draft?: CompanyLegalProfile;
  dirty: boolean;
  valid: boolean;
  editing: boolean;
  saving: boolean;
  companyId: string;
  onCancel: () => void;
  onEdit: () => void;
  onSave: () => void;
  onChange: (field: CompanyLegalEditableField, value: string) => void;
  onOpenSeal: () => void;
};

export function AffiliateLegalDetail({
  canWrite,
  canReadSeal,
  error,
  resource,
  draft,
  dirty,
  valid,
  editing,
  saving,
  companyId,
  onCancel,
  onEdit,
  onSave,
  onChange,
  onOpenSeal,
}: AffiliateLegalDetailProps) {
  return (
    <Stack data-testid="company-legal-detail-content" spacing={1.5}>
      {!canWrite ? <Alert severity="warning">법인 공식 정보를 조회할 수 있지만 변경 권한은 없습니다.</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <ResourceState loading={resource.loading} error={resource.error?.message} empty={false} onRetry={resource.reload}>
        {draft?.companyId === companyId ? (
          <>
            <CompanyLegalDetailCard
              canWrite={canWrite}
              profile={draft}
              dirty={dirty}
              valid={valid}
              editing={editing}
              saving={saving}
              editAriaLabel="계열사 기본 정보 수정"
              onCancel={onCancel}
              onEdit={onEdit}
              onSave={onSave}
              onChange={onChange}
            />
            {canReadSeal ? (
              <Paper
                variant="outlined"
                sx={{
                  overflow: 'hidden',
                }}
              >
                <SettingRow
                  title="증명서 직인"
                  value={draft.sealConfigured ? '등록됨' : '등록되지 않음'}
                  description="이 회사 명의 증명서에 사용하는 직인"
                  icon={<VerifiedOutlined fontSize="small" />}
                  ariaLabel={`${draft.descriptor} 증명서 직인 설정 열기`}
                  onClick={onOpenSeal}
                />
              </Paper>
            ) : null}
          </>
        ) : (
          <Alert severity="warning">선택한 법인 정보를 찾을 수 없습니다.</Alert>
        )}
      </ResourceState>
    </Stack>
  );
}
